<template>
  <ModalDialog
    ref="dialog"
    title="Multi Factor Auth Settings"
    icon="fa-mobile"
    sizeClass="modal-xl"
    :submitting="submitting"
    :submitDisabled="submitDisabled"
    submitTitle="Update"
    @closed="onClosed"
    @submitted="onSubmitted">

    <NotificationBar type="info">
      <NotificationBarTitle>Multifactor Authentication</NotificationBarTitle>
      Multifactor authentication (MFA) is an additional step for securing your user login and account. Once setup, user logins will require an additional code to access the system.<br />
      We <strong>strongly</strong> recommend users with account owner or administration rights protect their user login with multifactor authentication.<br />
      We recommend using the free <a href="https://googleauthenticator.net/" target="_blank">Google Authenticator</a> app available on iPhone and Android.<br />
    </NotificationBar>

    <FormGroup v-bind:form="form" :labelColumnSize="3">

      <!-- MFA Enable -->
      <div v-if="!isMFAEnabled">
        <FormRow label="Enable MFA">
          <FormSwitch v-model="setup.enable" booleanMode />
        </FormRow>

        <div v-show="setup.enable">
          <FormRow label="Confirm Password">
            <FormInputText type="password" v-model="setup.password" field="password" placeholder="Confirm Password" note="Enter your user password to enable multifactor authentication" />
          </FormRow>
          <FormHorizontalRule />
          <FormRow label="Device Name">
            <FormInputText v-model="setup.deviceName" field="deviceName" placeholder="Device Name" note="Name of the authenticator to help remember the associated device with login" />
          </FormRow>

          <Spinner v-if="setup.requesting" />

          <FormRow v-show="setup.secretKey && setup.qrLink" label="QR Code">
            <QRCode :value="setup.qrLink" /><br />
            View the above QR Code on your mobile device camera or authentication app to setup the authentication.<br />
            <div v-if="!setup.displaySecretKey">Alternatively, you can enter the secret key manually. <a href="#" @click.prevent="setup.displaySecretKey = true">Show Secret Key</a></div>
            <div v-if="setup.displaySecretKey" class="mt-1"><strong>Secret Key: </strong>&nbsp;&nbsp;{{setup.secretKey}}</div>
          </FormRow>

          <FormRow label="Token">
            <FormInputText v-model="setup.token" field="token" placeholder="Token" note="Once setup on your app, enter the current 6 digit token" />
          </FormRow>
        </div>
      </div>

      <!-- MFA Disable -->
      <div v-if="isMFAEnabled">
        <FormRow label="MFA Enabled">
          <FormSwitch v-model="current.enable" booleanMode />
        </FormRow>

        <div v-show="!current.enable">
          <FormRow label="Token">
            <FormInputText v-model="current.token" field="token" placeholder="Token" note="Enter the current 6 digit token to confirm disabling MFA" />
          </FormRow>
        </div>

      </div>


      
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'

import QRCode from '@/components/qrCode'

export default {
  name: 'UserMFASettingsDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
    QRCode
  },
  data() {
    return {
      user: { },
      form: {},
      setup: {
        enable: false,
        password: '',
        deviceName: '',
        secretKey: '',
        qrLink: '',
        token: '',
        displaySecretKey: false,
        requesting: false
      },
      current: {
        enable: true,
        token: ''
      },
      submitting: false
    }
  },
  watch: {
    'setup.enable': function() {
      if(!this.isMFAEnabled && this.setup.enable) {
        this.requestMFASecret()
      }
    }
  },
  mounted() {

  },
  computed: {
    isMFAEnabled() {
      return this.user.isMFAEnabled
    },
    submitDisabled() {
      return (!this.isMFAEnabled && !this.setup.enable) || (this.isMFAEnabled && this.current.enable)
    }
  },
  methods: {
    show(user) {
      // Reset form
      this.user = _.cloneDeep(user)
      this.form = {}
      this.submitting = false

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      
      if(!this.isMFAEnabled) {
        this.submitting = true

        let updateData = {
          password: this.setup.password,
          secret: this.setup.secretKey,
          deviceName: this.setup.deviceName,
          token: this.setup.token
        }

        this.$apiService.userService.enableUserMFA(updateData)
          .then(() => {
            this.submitting = false
            this.modalClosed(true)
            this.hide()
          })
          .catch(err => {
            this.submitting = false
            this.$data.form = this.processFormResponse(err)
          })
      } else {
        this.submitting = true

        let updateData = {
          token: this.current.token
        }

        this.$apiService.userService.disableUserMFA(updateData)
          .then(() => {
            this.submitting = false
            this.modalClosed(true)
            this.hide()
          })
          .catch(err => {
            this.submitting = false
            this.$data.form = this.processFormResponse(err)
          })
      }
    },
    requestMFASecret() {
      this.setup.requesting = true

      this.$apiService.userService.requestUserMFASecret()
      .then(response => {
        this.setup.secretKey = response.data.secretKey
        this.setup.qrLink = response.data.qrLink
      })
      .catch(err => {
        this.displayNetworkError(err, 'Error accessing MFA details')
      })
      .finally(() => {
        this.setup.requesting = false
      })
    }
  }
}
</script>